import React, { useState } from "react";
import { scripture } from "../lib/api";
import { toast } from "sonner";
import { BookOpenText } from "lucide-react";
import SaveWisdomButton from "../components/SaveWisdomButton";

export default function BiblePage() {
  const [q, setQ] = useState("");
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);

  const ask = async (e) => {
    e.preventDefault();
    if (!q.trim() || busy) return;
    setBusy(true);
    setR(null);
    try {
      const res = await scripture.ask(q);
      setR(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Let’s try that again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-7">
      <div className="fade-in-up">
        <h1 className="font-display text-3xl text-[#2E2E2E]">Sit with scripture</h1>
        <p className="text-sm text-[#6B6B6B] mt-1.5 leading-relaxed">A verse or a question — discover meaning, history, and how it speaks to today.</p>
      </div>

      <form onSubmit={ask} className="rounded-3xl border border-[#DCE5DB] bg-white p-6 space-y-4 shadow-[0_4px_12px_rgba(0,0,0,0.04)] fade-in-up stagger-1">
        <input
          data-testid="bible-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="“Romans 8:28” or “What does the Bible say about anxiety?”"
          className="w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3.5 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
        />
        <div className="flex justify-end">
          <button
            data-testid="bible-submit"
            type="submit"
            disabled={busy || !q.trim()}
            className="bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-5 py-3 text-sm font-medium inline-flex items-center gap-2 transition disabled:opacity-60 shadow-[0_4px_14px_rgba(79,111,82,0.18)]"
          >
            <BookOpenText size={14} /> {busy ? "Studying gently..." : "Open the verse"}
          </button>
        </div>
      </form>

      {busy && <div className="h-40 rounded-3xl shimmer" />}

      {r && (
        <div data-testid="bible-result" className="space-y-3 fade-in-up">
          <div className="rounded-3xl border border-[#DCE5DB] bg-[#EEF3EC] p-6 shadow-[0_4px_12px_rgba(0,0,0,0.04)] flex items-start justify-between gap-4 flex-wrap">
            <h2 className="font-display text-2xl text-[#4F6F52]">{r.reference}</h2>
            <SaveWisdomButton
              kind="scripture"
              referenceId={r.reference}
              title={r.reference}
              body={[
                r.meaning ? `Meaning: ${r.meaning}` : null,
                r.historical_context ? `Story behind it: ${r.historical_context}` : null,
                r.modern_application ? `Today: ${r.modern_application}` : null,
              ].filter(Boolean).join("\n\n")}
              meta={{ scripture_reference: r.reference }}
              testid="scripture-save-wisdom"
            />
          </div>
          <Card title="What it means" body={r.meaning} tone="white" />
          <Card title="The story behind it" body={r.historical_context} tone="cream" />
          <Card title="How it speaks to today" body={r.modern_application} tone="sage" />
        </div>
      )}
    </div>
  );
}

function Card({ title, body, tone }) {
  const styles = {
    white: "bg-white border-[#DCE5DB]",
    cream: "bg-[#EEF3EC] border-[#dce5db]",
    sage: "bg-[#E1ECDD] border-[#d3e0d0]",
  };
  return (
    <div className={`rounded-3xl border p-6 shadow-[0_4px_12px_rgba(0,0,0,0.04)] ${styles[tone]}`}>
      <h3 className="font-display text-lg text-[#2E2E2E] mb-2">{title}</h3>
      <p className="text-[15px] leading-[1.8] text-[#2E2E2E]">{body}</p>
    </div>
  );
}
